import { useEffect, useMemo, useState } from "react";

import { Input } from "@/components/ui/input";

import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

import { Label } from "@/components/ui/label";

import { StepShell, FormField } from "./StepShell";

import { EMPLOYMENT_TYPES } from "@/constants/employmentTypes";

/*
 * --------------------------------------------------------------------------
 * Employment Fields
 * --------------------------------------------------------------------------
 */

const EMPLOYMENT_FIELDS = [
  {
    key: "officeName",
    label: "Office Name",
    required: true,
  },
  {
    key: "officeAddress",
    label: "Office Address",
    textarea: true,
    required: true,
  },
  {
    key: "officePincode",
    label: "Office Pincode",
    required: true,
    inputMode: "numeric",
    maxLength: 6,
  },
  {
    key: "employerName",
    label: "Employer Name",
    required: true,
  },
  {
    key: "employerAddress",
    label: "Employer Address",
    textarea: true,
    required: true,
  },
  {
    key: "employerDesignation",
    label: "Employer Designation",
    required: true,
  },
];

/*
 * --------------------------------------------------------------------------
 * Experience Fields
 * --------------------------------------------------------------------------
 */

const EXPERIENCE_FIELDS = [
  {
    key: "totalWorkExperienceMonths",
    label: "Total Work Experience (months)",
    inputMode: "numeric",
    min: 0,
  },
  {
    key: "currentJobPeriodMonths",
    label: "Current Job Period (months)",
    inputMode: "numeric",
    min: 0,
  },
];

/*
 * --------------------------------------------------------------------------
 * Helpers
 * --------------------------------------------------------------------------
 */

function normalizeData(data = {}) {
  return {
    employmentType: data.employmentType ?? "",

    officeName: data.officeName ?? "",

    officeAddress: data.officeAddress ?? "",

    officePincode: data.officePincode ?? "",

    employerName: data.employerName ?? "",

    employerAddress: data.employerAddress ?? "",

    employerDesignation: data.employerDesignation ?? "",

    totalWorkExperienceMonths: data.totalWorkExperienceMonths ?? "",

    currentJobPeriodMonths: data.currentJobPeriodMonths ?? "",
  };
}

function isFilled(value) {
  if (value === null || value === undefined) {
    return false;
  }

  if (typeof value === "string") {
    return value.trim().length > 0;
  }

  return true;
}

/*
 * --------------------------------------------------------------------------
 * Employment Section
 * --------------------------------------------------------------------------
 */

function EmploymentSection({
  applicant,
  data,
  onTypeChange,
  onFieldChange,
  testIdPrefix,
  readOnlyEmploymentType = false,
}) {
  const type = data.employmentType;

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5">
      {/* ------------------------------------------------------------------ */}
      {/* Employment Type                                                     */}
      {/* ------------------------------------------------------------------ */}

      <div className="mb-6">
        <h3 className="text-sm font-semibold text-[#0F2D52] mb-3">
          Employment Type
        </h3>

        {readOnlyEmploymentType ? (
          <div>
            <div className="flex min-h-11 items-center rounded-xl border border-gray-200 bg-gray-50 px-4 text-sm font-semibold text-[#0F2D52]">
              {EMPLOYMENT_TYPES.find((item) => item.value === type)?.label ||
                "—"}
            </div>

            <p className="mt-2 text-xs text-gray-500">
              Employment type is taken from your application and cannot be
              changed here.
            </p>
          </div>
        ) : (
          <RadioGroup
            value={type}
            onValueChange={onTypeChange}
            className="grid grid-cols-1 sm:grid-cols-3 gap-3"
          >
            {EMPLOYMENT_TYPES.map((item) => {
              const selected = type === item.value;

              return (
                <label
                  key={item.value}
                  data-testid={`${testIdPrefix}-emp-type-${item.value.toLowerCase()}`}
                  className={`cursor-pointer rounded-xl border p-4 flex items-center gap-3 mm-transition ${
                    selected
                      ? "border-[#0F2D52] bg-[#0F2D52]/5"
                      : "border-gray-200 hover:border-[#1E5AA8]"
                  }`}
                >
                  <RadioGroupItem
                    value={item.value}
                    className="border-gray-400 data-[state=checked]:border-[#0F2D52] data-[state=checked]:text-[#0F2D52]"
                  />

                  <Label className="text-sm font-semibold text-[#0F2D52] cursor-pointer">
                    {item.label}
                  </Label>
                </label>
              );
            })}
          </RadioGroup>
        )}
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Office / Employer Details                                           */}
      {/* ------------------------------------------------------------------ */}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {EMPLOYMENT_FIELDS.map((field) => (
          <FormField
            key={field.key}
            label={field.label}
            required={field.required}
            className={field.full ? "md:col-span-2" : ""}
          >
            {field.textarea ? (
              <textarea
                data-testid={`${testIdPrefix}-emp-${field.key}`}
                value={data[field.key] ?? ""}
                onChange={(event) =>
                  onFieldChange(field.key, event.target.value)
                }
                rows={3}
                className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#1E5AA8] focus:ring-1 focus:ring-[#1E5AA8]"
              />
            ) : (
              <Input
                type="text"
                data-testid={`${testIdPrefix}-emp-${field.key}`}
                inputMode={field.inputMode}
                maxLength={field.maxLength}
                value={data[field.key] ?? ""}
                onChange={(event) => {
                  let value = event.target.value;

                  if (field.inputMode === "numeric") {
                    value = value
                      .replace(/\D/g, "")
                      .slice(0, field.maxLength || 20);
                  }

                  onFieldChange(field.key, value);
                }}
                className="h-11 rounded-xl border-gray-300 focus-visible:ring-[#1E5AA8]"
              />
            )}
          </FormField>
        ))}
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Work Experience                                                     */}
      {/* ------------------------------------------------------------------ */}

      <div className="mt-6">
        <h3 className="text-sm font-semibold text-[#0F2D52] mb-3">
          Work Experience
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {EXPERIENCE_FIELDS.map((field) => (
            <FormField key={field.key} label={field.label}>
              <Input
                type="number"
                min={field.min}
                value={data[field.key] ?? ""}
                onChange={(event) =>
                  onFieldChange(field.key, event.target.value)
                }
                className="h-11 rounded-xl border-gray-300"
              />
            </FormField>
          ))}
        </div>
      </div>
    </div>
  );
}

/*
 * --------------------------------------------------------------------------
 * Component
 * --------------------------------------------------------------------------
 */

export default function EmploymentStep({ state, patch, setStep }) {
  /*
   * Primary applicant employment.
   *
   * The primary applicant's employment data is exposed through
   * employment_details for backwards compatibility.
   */
  const primaryEmployment = useMemo(
    () => normalizeData(state?.employment_details || {}),
    [state?.employment_details],
  );

  /*
   * Co-applicant employment.
   *
   * The co-applicant object already contains the employment
   * fields because ApplicationPortalService maps the complete
   * applicant record into co_applicant.
   */
  const coApplicantExists = Boolean(state?.co_applicant || state?.coApplicant);

  const coApplicantEmployment = useMemo(
    () =>
      coApplicantExists
        ? normalizeData(state?.co_applicant || state?.coApplicant || {})
        : null,
    [state, coApplicantExists],
  );

  const [primary, setPrimary] = useState(primaryEmployment);

  const [coApplicant, setCoApplicant] = useState(coApplicantEmployment);

  const [saving, setSaving] = useState(false);

  /*
   * ------------------------------------------------------------------------
   * Synchronize with backend state
   * ------------------------------------------------------------------------
   */

  useEffect(() => {
    setPrimary(primaryEmployment);
  }, [primaryEmployment]);

  useEffect(() => {
    setCoApplicant(coApplicantEmployment);
  }, [coApplicantEmployment]);

  /*
   * ------------------------------------------------------------------------
   * Field updates
   * ------------------------------------------------------------------------
   */

  const updatePrimary = (key, value) => {
    setPrimary((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const updateCoApplicant = (key, value) => {
    setCoApplicant((current) => ({
      ...current,
      [key]: value,
    }));
  };

  /*
   * ------------------------------------------------------------------------
   * Validation
   * ------------------------------------------------------------------------
   */

  const isEmploymentComplete = (data) => {
    if (!data) {
      return false;
    }

    const validType = EMPLOYMENT_TYPES.some(
      (item) => item.value === data.employmentType,
    );

    if (!validType) {
      return false;
    }

    return (
      isFilled(data.officeName) &&
      isFilled(data.officeAddress) &&
      isFilled(data.officePincode) &&
      isFilled(data.employerName) &&
      isFilled(data.employerAddress) &&
      isFilled(data.employerDesignation)
    );
  };

  const valid = useMemo(() => {
    if (!isEmploymentComplete(primary)) {
      return false;
    }

    /*
     * If a co-applicant exists, their employment
     * details are mandatory as well.
     */
    if (coApplicantExists) {
      return isEmploymentComplete(coApplicant);
    }

    return true;
  }, [primary, coApplicant, coApplicantExists]);

  /*
   * ------------------------------------------------------------------------
   * Payload
   * ------------------------------------------------------------------------
   */

  const buildApplicantPayload = (
    data,
    { includeEmploymentType = true } = {},
  ) => {
    const payload = {
      totalWorkExperienceMonths: data.totalWorkExperienceMonths,

      currentJobPeriodMonths: data.currentJobPeriodMonths,

      officeName: data.officeName,

      officeAddress: data.officeAddress,

      officePincode: data.officePincode,

      employerName: data.employerName,

      employerAddress: data.employerAddress,

      employerDesignation: data.employerDesignation,
    };

    if (includeEmploymentType) {
      payload.employmentType = data.employmentType;
    }

    return payload;
  };

  /*
   * ------------------------------------------------------------------------
   * Save & Continue
   * ------------------------------------------------------------------------
   */

  const save = async () => {
    if (saving || !valid) {
      return;
    }

    setSaving(true);

    try {
      const payload = {
        primaryApplicant: buildApplicantPayload(primary, {
          includeEmploymentType: false,
        }),
      };

      if (coApplicantExists && coApplicant) {
        payload.coApplicant = buildApplicantPayload(coApplicant, {
          includeEmploymentType: true,
        });
      }

      await patch("employment_details", payload);

      await setStep(3);
    } finally {
      setSaving(false);
    }
  };

  return (
    <StepShell
      testId="portal-employment"
      title="Employment Details"
      subtitle="Provide your and the co-applicant's current employment or business details, including your occupation, employer/business information and work address."
      onBack={() => setStep(1)}
      onNext={save}
      nextLabel={saving ? "Saving..." : "Save & Continue"}
      disableNext={saving || !valid}
    >
      {/* ------------------------------------------------------------------ */}
      {/* Primary Applicant                                                   */}
      {/* ------------------------------------------------------------------ */}

      <div className="mb-8">
        <div className="mb-4">
          <h2 className="text-base font-semibold text-[#0F2D52]">
            Primary Applicant
          </h2>

          {state?.personal_details?.fullName && (
            <p className="text-sm text-gray-500 mt-1">
              {state.personal_details.fullName}
            </p>
          )}
        </div>

        <EmploymentSection
          applicant="primary"
          data={primary}
          readOnlyEmploymentType
          onTypeChange={undefined}
          onFieldChange={updatePrimary}
          testIdPrefix="primary"
        />
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Co-Applicant                                                        */}
      {/* ------------------------------------------------------------------ */}

      {coApplicantExists && (
        <div>
          <div className="mb-4">
            <h2 className="text-base font-semibold text-[#0F2D52]">
              Co-Applicant
            </h2>

            {(state?.co_applicant?.fullName ||
              state?.coApplicant?.fullName) && (
              <p className="text-sm text-gray-500 mt-1">
                {(state?.co_applicant || state?.coApplicant).fullName}
              </p>
            )}
          </div>

          <EmploymentSection
            applicant="coApplicant"
            data={coApplicant || normalizeData()}
            onTypeChange={(value) => updateCoApplicant("employmentType", value)}
            onFieldChange={updateCoApplicant}
            testIdPrefix="co-applicant"
          />
        </div>
      )}
    </StepShell>
  );
}
